import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  Invoice,
  InvoiceTemplate,
  InvoiceTemplateCreate,
  InvoiceTemplateUpdate,
  InvoiceReport,
  InvoiceUpdate,
  PaymentCreate,
} from '@vuokramani/shared';
import { api } from './api.ts';
import { meQueryKey } from './auth.ts';

const BASE = import.meta.env.VITE_API_URL ?? '/api';

/** Laskut, pohjat ja loki muuttuvat yhdessä. */
function invalidate(queryClient: QueryClient, propertyId: string) {
  for (const key of [
    ['invoices', propertyId],
    ['invoiceTemplates'],
    ['events', propertyId],
    ['tasks'],
  ]) {
    void queryClient.invalidateQueries({ queryKey: key });
  }
}

export function useInvoiceTemplates(leaseId: string | undefined) {
  return useQuery({
    queryKey: ['invoiceTemplates', leaseId],
    queryFn: () =>
      api<{ templates: InvoiceTemplate[] }>(`/leases/${leaseId}/invoice-templates`).then(
        (r) => r.templates,
      ),
    enabled: Boolean(leaseId),
  });
}

export function useCreateTemplate(propertyId: string, leaseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: InvoiceTemplateCreate) =>
      api<InvoiceTemplate>(`/leases/${leaseId}/invoice-templates`, { method: 'POST', body }),
    onSuccess: () => {
      invalidate(queryClient, propertyId);
      // Tilinumero tallentuu käyttäjälle.
      void queryClient.invalidateQueries({ queryKey: meQueryKey });
    },
  });
}

export function useDeleteTemplate(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/invoice-templates/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidate(queryClient, propertyId),
  });
}

export function useUpdateTemplate(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: InvoiceTemplateUpdate }) =>
      api<InvoiceTemplate>(`/invoice-templates/${id}`, { method: 'PATCH', body }),
    onSuccess: () => invalidate(queryClient, propertyId),
  });
}

/** Asunnon laskut vuodelta. Puuttuvat kuukaudet luodaan palvelimella samalla. */
export function useInvoices(propertyId: string | undefined, year: number) {
  return useQuery({
    queryKey: ['invoices', propertyId, year],
    queryFn: () =>
      api<{ invoices: Invoice[] }>(`/properties/${propertyId}/invoices?year=${year}`).then(
        (r) => r.invoices,
      ),
    enabled: Boolean(propertyId),
  });
}

export function useUpdateInvoice(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: InvoiceUpdate }) =>
      api<Invoice>(`/invoices/${id}`, { method: 'PUT', body }),
    onSuccess: () => invalidate(queryClient, propertyId),
  });
}

export function useAddPayment(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PaymentCreate }) =>
      api<Invoice>(`/invoices/${id}/payments`, { method: 'POST', body }),
    onSuccess: () => invalidate(queryClient, propertyId),
  });
}

export function useDeletePayment(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/invoice-payments/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidate(queryClient, propertyId),
  });
}

export function useSettleOverdue(propertyId: string, leaseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ settled: number }>(`/leases/${leaseId}/invoices/settle-overdue`, { method: 'POST' }),
    onSuccess: () => invalidate(queryClient, propertyId),
  });
}

/** Vuokralainen: "Maksoin" (tai ilmoituksen peruminen, paidOn = null). */
export function useReportPaid(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: InvoiceReport }) =>
      api<Invoice>(`/invoices/${id}/report`, { method: 'POST', body }),
    onSuccess: () => invalidate(queryClient, propertyId),
  });
}

/** Vuokranantaja: rahaa ei tullut. */
export function useRejectReport(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<Invoice>(`/invoices/${id}/reject-report`, { method: 'POST' }),
    onSuccess: () => invalidate(queryClient, propertyId),
  });
}

export function invoicePdfUrl(id: string, download = false): string {
  return `${BASE}/invoices/${id}/pdf${download ? '?download=1' : ''}`;
}

/** "2026-10" → "lokakuu 2026". */
export function monthLabel(month: string, locale: string): string {
  const [y, m] = month.split('-').map(Number);
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(
    new Date(y!, m! - 1, 1),
  );
}

/** "2026-10" + n kuukautta. */
export function addMonths(month: string, n: number): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y!, m! - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
